/**
 * Guest Capture Policy — client mirror of public/api/capture-policy-helper.php.
 *
 * Decides how much a guest is asked for before an estimate can be saved, and
 * whether the detailed materials list is gated behind that form at all.
 *
 *   full    — today's behaviour: ten required fields (GuestEstimateModal), and
 *             the BOM stays hidden from guests until they submit.
 *   minimal — estimate name + email only (GuestSaveModal), and the gate comes
 *             OFF: the guest sees the whole estimate whether they save or not.
 *             Saving buys retrieval and the emailed link, nothing else.
 *
 * Resolved per retailer and delivered with the price book
 * (products.php?pricing=1&retailer_id=N), NOT with the branded-portal payload —
 * retailer-by-subdomain.php 404s for non-partner retailers, so a policy set on a
 * plain retailer would never arrive. Riding with pricing means the policy follows
 * whichever retailer is supplying prices, on a branded portal or on Trusscore's.
 *
 * The server re-resolves and re-enforces this on every save. Nothing here is a
 * security control — it only decides what the form renders.
 */

export type CaptureMode = 'full' | 'minimal';
export type FieldState = 'hidden' | 'optional' | 'required';

export type CaptureFieldName =
  | 'estimateName' | 'firstName' | 'lastName' | 'email' | 'phone'
  | 'userCategory' | 'projectType' | 'country' | 'region' | 'city' | 'postalCode';

export interface CapturePolicy {
  mode: CaptureMode;
  /** Whether the guest may decline to save and still use the estimate. */
  allowSkip: boolean;
  /** Retailer-authored override for the explanatory line; null = use the default. */
  blurb: string | null;
  fields: Record<CaptureFieldName, FieldState>;
}

/** Mirrors cpDefaultPolicy() in capture-policy-helper.php. */
export const DEFAULT_CAPTURE_POLICY: CapturePolicy = {
  mode: 'full',
  allowSkip: false,
  blurb: null,
  fields: {
    estimateName: 'required',
    firstName: 'required',
    lastName: 'required',
    email: 'required',
    phone: 'required',
    userCategory: 'required',
    projectType: 'required',
    country: 'required',
    region: 'optional',
    city: 'required',
    postalCode: 'required',
  },
};

/**
 * The line shown under the email field in minimal mode.
 *
 * Deliberately says what the address is FOR and nothing more. It does not
 * promise "we won't email you anything else" — on a full-capture retailer the
 * lead does go to Pardot, and a blanket promise would be false. This copy has to
 * survive a retailer's legal review, so it must stay literally true.
 *
 * TRANSLATIONS OF THIS STRING CARRY THE SAME OBLIGATION. It is a privacy
 * disclosure, not marketing copy: a translation that drifts into promising more
 * than the English does is a compliance problem, not a wording preference. Any
 * new locale's version of results:save.captureBlurb needs the same review the
 * English got.
 *
 * A retailer may override it entirely via `policy.blurb`, which is
 * admin-authored database text and belongs to the Phase 3 content overlay.
 */
export const DEFAULT_CAPTURE_BLURB_KEY = 'results:save.captureBlurb';

/** Tolerant coercion of a server payload into a usable policy. */
export function normalizeCapturePolicy(raw: unknown): CapturePolicy {
  if (!raw || typeof raw !== 'object') return DEFAULT_CAPTURE_POLICY;
  const r = raw as Partial<CapturePolicy>;
  const mode: CaptureMode = r.mode === 'minimal' ? 'minimal' : 'full';

  const fields = { ...DEFAULT_CAPTURE_POLICY.fields };
  if (r.fields && typeof r.fields === 'object') {
    (Object.keys(fields) as CaptureFieldName[]).forEach((name) => {
      const v = (r.fields as Record<string, unknown>)[name];
      if (v === 'hidden' || v === 'optional' || v === 'required') fields[name] = v;
    });
  }
  // Server enforces this too; keep the client honest so the form can never
  // render without the one field the whole flow depends on.
  fields.email = 'required';

  return {
    mode,
    allowSkip: typeof r.allowSkip === 'boolean' ? r.allowSkip : mode === 'minimal',
    blurb: typeof r.blurb === 'string' && r.blurb.trim() !== '' ? r.blurb : null,
    fields,
  };
}

/** True when guests should see the full materials list without saving. */
export const gateIsOpen = (p: CapturePolicy): boolean => p.mode === 'minimal';
